
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Card } from 'react-bootstrap';
import { toast } from 'react-toastify';
import config from "../../config/config";
import axios from "axios";
import { getAuthToken } from "../../utils/token";

const MaterialRejectDetailsModal = ({ show, handleClose, materialId }) => {
    const [materials, setMaterials] = useState(null);

    const [form, setForm] = useState({
        material_id: '',
        expected_qty: '',
        supplier_name: '',
        supplier_contact: '',
        inspection_date: '',
        visual_inspection: false,      
        dimensional_check: false,      
        quantity_check: false,         
        packaging_condition: false,    
        material_certificate: false,   
        actual_quantity: '',
        quantity_rejected: '',
        decision: '',
        notes: ''
    });
    useEffect(() => {
        if (show && materialId) {
            fetchMaterial(materialId);
        }
    }, [show, materialId]);

    const fetchMaterial = async (materialId) => {
        const token = getAuthToken();
        if (!token) return;
        try {
            const response = await axios.get(`${config.API}/material/${materialId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            const mat = response?.data?.material;
            if (mat) {
                setMaterials(mat);
                setForm((prev) => ({
                    ...prev,
                    material_id: mat.id,
                    expected_qty: mat.quantity,
                    supplier_name: mat.supplier_name || '',
                    supplier_contact: mat.supplier_contact || '',
                    inspection_date: new Date().toISOString().split("T")[0] 
                }));
            }
        } catch (err) {
            console.error("Error fetching material:", err.message);
            setMaterials(null);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

  const handleSubmit = async (e) => {
    e.preventDefault(); // prevent form from refreshing
    const token = getAuthToken();
    if (!token) {
        toast.error("Authentication token missing!");
        return;
    }

    try {
        const payload = {
            material_id: Number(materials?.material_id),
            expected_qty: Number(materials?.quantity),
            supplier_name: materials?.supplier_name || 'N/A',
            supplier_contact: form.supplier_contact,
            inspection_date: form.inspection_date,
            visual_inspection: form.visual_inspection,
            dimensional_check: form.dimensional_check,
            quantity_check: form.quantity_check,
            packaging_condition: form.packaging_condition,
            material_certificate: form.material_certificate,
            actual_quantity: Number(form.actual_quantity),
            quantity_rejected: Number(form.quantity_rejected),
            decision: form.decision,
            notes: form.notes
        };

        const response = await axios.post(
            `${config.API}/supervisor/inspection/create`,
            payload,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            }
        );

        if (response.data) {
            toast.success(response.data.message || "Inspection report submitted!");
            setForm({
                material_id: '',
                expected_qty: '',
                supplier_name: '',
                supplier_contact: '',
                inspection_date: '',
                visual_inspection: false,
                dimensional_check: false,
                quantity_check: false,
                packaging_condition: false,
                material_certificate: false,
                actual_quantity: '',
                quantity_rejected: '',
                decision: '',
                notes: ''
            });
            handleClose();
        } else {
            toast.error(response.data.message || "Something went wrong!");
        }
    } catch (err) {
        console.error("Error submitting inspection report:", err.message);
        toast.error("Failed to submit inspection report!");
    }
};

    return (
        <Modal show={show} onHide={handleClose} centered size="lg" className='font_family'>
            <Modal.Header closeButton className='border-0 p-4'>
                <strong className='fs-4'>Material Inspection Report</strong>
            </Modal.Header>
            <Modal.Body className='p-4'>
                {/* Material Info */}
                <Card className="mb-3">
                    <Card.Body>
                        <Row>
                            <Col xs={6}><strong>ID:</strong> {materials?.material_id}</Col>
                            <Col xs={6}><strong>Material:</strong> {materials?.material_name}</Col>
                        </Row>
                        <Row className="mt-2">
                            <Col xs={6}><strong>Expected Qty:</strong> {materials?.quantity}</Col>
                            <Col xs={6}><strong>Supplier:</strong> {materials?.supplier_name || 'N/A'}</Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Checklist */}
                <Card className="mb-3">
                    <Card.Body>
                        <Form.Check type="checkbox" label="Visual Inspection" name="visual_inspection" checked={form.visual_inspection} onChange={handleChange} />
                        <Form.Check type="checkbox" label="Dimensional Check" name="dimensional_check" checked={form.dimensional_check} onChange={handleChange} />
                        <Form.Check type="checkbox" label="Quantity Check" name="quantity_check" checked={form.quantity_check} onChange={handleChange} />
                        <Form.Check type="checkbox" label="Packaging Condition" name="packaging_condition" checked={form.packaging_condition} onChange={handleChange} />
                        <Form.Check type="checkbox" label="Material Certificate" name="material_certificate" checked={form.material_certificate} onChange={handleChange} />
                    </Card.Body>
                </Card>

                {/* Actual & Rejected Qty */}
                <Form.Group as={Row} className="align-items-center mb-3 ms-3">
                    <Form.Label column sm={3}>Actual Quantity</Form.Label>
                    <Col sm={2}>
                        <Form.Control
                            type="number"
                            name="actual_quantity"
                            value={form.actual_quantity}
                            onChange={handleChange}
                        />
                    </Col>
                    <Col sm={1}>pcs</Col>

                    <Form.Label column sm={3} className="text-end">Quantity Rejected</Form.Label>
                    <Col sm={2}>
                        <Form.Control
                            type="number"
                            name="quantity_rejected"
                            value={form.quantity_rejected}
                            onChange={handleChange}
                        />
                    </Col>
                    <Col sm={1}>pcs</Col>
                </Form.Group>

                {/* Decision */}
                <Form.Group as={Row} className="mb-3 ms-5">
                    <Form.Label column md={2}>Decision</Form.Label>
                    <Col md={10}>
                        <Form.Select
                            name="decision"
                            value={form.decision}
                            onChange={handleChange}
                        >
                            <option value="">Select decision</option>
                            <option value="Approved">Accept Material</option>
                            <option value="Rejected">Reject Material</option>
                            <option value="Hold">Hold for Review</option>
                        </Form.Select>
                    </Col>
                </Form.Group>

                {/* Notes */}
                <Form.Group as={Row} className="mb-3 ms-5">
                    <Form.Label column md={2}>Notes</Form.Label>
                    <Col md={10}>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            name="notes"
                            value={form.notes}
                            onChange={handleChange}
                            placeholder="Additional notes or observations"
                        />
                    </Col>
                </Form.Group>
            </Modal.Body>
            <Modal.Footer className='border-0'>
                <Button variant="outline-secondary" onClick={handleClose}>
                    Cancel
                </Button>
                <Button variant="dark" onClick={handleSubmit}>
                    Reject Material
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default MaterialRejectDetailsModal;

